import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { BoxShadowModifierValue } from "schemas/generated/fileModifiers";

import React, { useMemo } from "react";

import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

const SHADOW_OPTIONS = [
  {
    value: "none",
    label: "None",
    displayValue: "None",
  },
  {
    value: "2xs",
    label: "2XS",
    displayValue: "2XS",
  },
  {
    value: "xs",
    label: "XS",
    displayValue: "XS",
  },
  {
    value: "sm",
    label: "Small",
    displayValue: "Small",
  },
  {
    value: "md",
    label: "Medium",
    displayValue: "Medium",
  },
  {
    value: "lg",
    label: "Large",
    displayValue: "Large",
  },
  {
    value: "xl",
    label: "Extra Large",
    displayValue: "Extra Large",
  },
  {
    value: "2xl",
    label: "2XL",
    displayValue: "2XL",
  },
] as const;

interface ShadowModifierProps {
  onChange: (modifiers: BoxShadowModifierValue[]) => void;
}

const ShadowModifierComponent: React.FC<ShadowModifierProps> = ({
  onChange,
}) => {
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const extractedValue = useMemo(() => {
    if (!defaultDesignation) {
      return "none";
    }

    // NOTE (Kevin, 2025-07-29): We cannot use computedStyles.boxShadow because the end result with all the px values
    // and color values would be too difficult to parse and convert back into a Tailwind shadow type.
    // Instead, we parse the className to find Tailwind shadow classes. If no matching class is found
    // but there exists a computed boxShadow value, we show "Custom" in the UI (but don't add it as
    // a selectable option). When a user selects a real shadow option from "Custom" state, it works
    // normally. When they select "None" from "Custom" state, it removes the shadow.
    const className = defaultDesignation.element.props?.className;
    if (className && typeof className === "string") {
      const classes = className.split(" ").filter(Boolean);

      for (const cls of classes) {
        if (cls === "shadow") {
          return "md";
        }
        if (cls.startsWith("shadow-")) {
          const shadowValue = cls.replace("shadow-", "");
          if (SHADOW_OPTIONS.some((option) => option.value === shadowValue)) {
            return shadowValue;
          }
        }
      }
    }

    // Check if there's a computed boxShadow value that doesn't match our classes
    const computedShadow = defaultDesignation.element.styleOverrides?.boxShadow;
    if (
      computedShadow &&
      computedShadow !== "none" &&
      computedShadow !== "0 0 #0000"
    ) {
      return "custom";
    }

    return "none";
  }, [defaultDesignation]);

  const [currentValue, setCurrentValue] = useOverridableState(
    extractedValue,
    useDebouncedCallback((shadowValue: string) => {
      if (SHADOW_OPTIONS.some((option) => option.value === shadowValue)) {
        onChange([
          {
            type: "boxShadow" as const,
            value: shadowValue as BoxShadowModifierValue["value"],
          },
        ]);
      }
    }, 300),
  );

  const items: ComboboxItemWithValue[] = SHADOW_OPTIONS.map((shadow) => ({
    value: shadow.value,
    label: shadow.label,
  }));

  const effectiveValue = currentValue === "custom" ? "none" : currentValue;
  const selectedItem =
    items.find((item) => item.value === effectiveValue) ?? null;

  const displayLabel =
    currentValue === "custom" ? "Custom" : selectedItem?.label ?? "None";
  const isPlaceholder = currentValue === null || currentValue === "none";

  return (
    <div className="shadow-modifier">
      <ComboboxRoot
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          setCurrentValue(item?.value ?? "none");
        }}
        itemToStringValue={(item) => String(item.label)}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxTriggerButton className="w-full" isPlaceholder={isPlaceholder}>
          {displayLabel}
        </ComboboxTriggerButton>
        <ComboboxContent sideOffset={8} side="top" align="center">
          <ComboboxList>
            {(item: ComboboxItemWithValue) => (
              <ComboboxItem key={item.value} {...item} value={item} />
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

export default ShadowModifierComponent;
